import React, { useState, useEffect } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import api from "@/lib/api";
import MaintenancePage from "@/pages/MaintenancePage";
import CookieBanner from "@/components/CookieBanner";
import AccessibilityWidget from "@/components/AccessibilityWidget";
import { openSettings } from "@/lib/cookieConsent";

const DETMOLD_LOGO = "/assets/logo-detmold.png";
const SKYLINE_FOOTER = "/assets/skyline-baustelle.png";

function NavItem({ to, children, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `text-sm font-medium transition-colors hover:text-[hsl(var(--brand-green-strong))] ${
          isActive ? "text-[hsl(var(--brand-green-strong))]" : "text-neutral-800"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [bypass, setBypass] = useState(() => !!localStorage.getItem("maintenance_bypass"));
  const { pathname, hash, key } = useLocation();

  // Load site settings (maintenance toggle)
  useEffect(() => {
    api.get("/site/settings")
      .then((r) => setSiteSettings(r.data || {}))
      .catch(() => setSiteSettings({ maintenance_enabled: false }));
  }, []);

  // On route change, scroll to top (unless a hash anchor is present).
  // Without this, navigating to a long page (e.g. /liveverkehr) keeps the
  // browser's prior scroll position, dropping the user mid-page.
  useEffect(() => {
    if (hash) return; // hash effect below handles anchor scrolling
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, key, hash]);

  // Scroll to hash anchor on hash change (react-router doesn't do this by default).
  // Retries a few times because the target element might be rendered after an
  // async API call (e.g. #newsletter at the bottom of Home).
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    let tries = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (tries++ < 20) setTimeout(tick, 100); // up to ~2s
    };
    const t = setTimeout(tick, 50);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pathname, hash, key]);

  // While settings load, render nothing (avoid flicker)
  if (siteSettings === null) return null;

  // Show maintenance page if enabled and no bypass + not admin
  if (siteSettings.maintenance_enabled && !bypass) {
    return (
      <MaintenancePage
        settings={siteSettings}
        onUnlock={() => setBypass(true)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white text-neutral-900 flex flex-col qht-public">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-brand-line">
        <div className="container-narrow flex items-center justify-between h-[88px]">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img src={DETMOLD_LOGO} alt="Detmold" className="h-11 w-auto" />
            <span className="hidden sm:block text-[11px] leading-tight text-neutral-700 border-l border-brand-line pl-3">
              Kulturstadt<br />im Teutoburger Wald
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            <NavItem to="/news" testid="nav-aktuelles">Aktuelles</NavItem>
            <NavItem to="/#termine" testid="nav-termine">Termine</NavItem>
            <div
              className="relative"
              onMouseEnter={() => setInfoOpen(true)}
              onMouseLeave={() => setInfoOpen(false)}
            >
              <NavLink
                to="/informationen"
                data-testid="nav-infos"
                className={({ isActive }) =>
                  `inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-[hsl(var(--brand-green-strong))] ${
                    isActive ? "text-[hsl(var(--brand-green-strong))]" : "text-neutral-800"
                  }`
                }
              >
                Infos <ChevronDown className="h-3.5 w-3.5" />
              </NavLink>
              {infoOpen && (
                <div className="absolute left-0 top-full pt-2">
                  <div className="bg-white border border-brand-line shadow-sm py-2 w-56">
                    <Link to="/informationen" data-testid="nav-infos-projekt" className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50">Über das Projekt</Link>
                    <Link to="/#baufortschritt" data-testid="nav-infos-baufortschritt" className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50">Baufortschritt</Link>
                    <Link to="/bauchroniken" data-testid="nav-infos-bauchroniken" className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50">Bauchroniken</Link>
                    <Link to="/partner" data-testid="nav-infos-kontakt" className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50">Beteiligung &amp; Kontakt</Link>
                  </div>
                </div>
              )}
            </div>
            <NavItem to="/faq" testid="nav-faq">FAQ</NavItem>
            <NavItem to="/partner" testid="nav-partner">Partner</NavItem>
            <NavItem to="/kontakt" testid="nav-kontakt">Kontakt</NavItem>
            <NavLink
              to="/liveverkehr"
              data-testid="nav-liveverkehr"
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  isActive
                    ? "bg-[hsl(var(--brand-green-dark))] text-white border-[hsl(var(--brand-green-dark))]"
                    : "text-[hsl(var(--brand-green-dark))] border-[hsl(var(--brand-green))] hover:bg-[hsl(var(--brand-soft))]"
                }`
              }
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-green))]" />
              LIVE-Verkehr
            </NavLink>
            <NavLink
              to="/berufsverkehr-ticker"
              data-testid="nav-berufsverkehr-ticker"
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full transition-colors ${
                  isActive
                    ? "bg-[hsl(var(--brand-green-dark))] text-white"
                    : "text-[hsl(var(--brand-green-strong))] hover:bg-[hsl(var(--brand-soft))]"
                }`
              }
            >
              Berufsverkehr-Ticker
            </NavLink>
            <NavLink
              to="/#newsletter"
              data-testid="nav-newsletter"
              className="text-sm font-medium text-[hsl(var(--brand-green-strong))] hover:underline"
            >
              Newsletter
            </NavLink>
          </nav>

          <button className="md:hidden p-2 text-neutral-700" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menü">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <nav className="md:hidden border-t border-brand-line bg-white">
            <div className="container-narrow py-4 flex flex-col gap-3">
              <Link to="/news" onClick={() => setOpen(false)} className="text-sm text-neutral-800 py-1">Aktuelles</Link>
              <Link to="/#termine" onClick={() => setOpen(false)} className="text-sm text-neutral-800 py-1">Termine</Link>
              <div className="pl-1 border-l border-brand-line ml-1 pl-3 space-y-1.5 py-1">
                <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-400">Infos</div>
                <Link to="/informationen" onClick={() => setOpen(false)} data-testid="mobile-nav-projekt" className="block text-sm text-neutral-800">Über das Projekt</Link>
                <Link to="/#baufortschritt" onClick={() => setOpen(false)} data-testid="mobile-nav-baufortschritt" className="block text-sm text-neutral-800">Baufortschritt</Link>
                <Link to="/bauchroniken" onClick={() => setOpen(false)} data-testid="mobile-nav-bauchroniken" className="block text-sm text-neutral-800">Bauchroniken</Link>
                <Link to="/partner" onClick={() => setOpen(false)} data-testid="mobile-nav-kontakt" className="block text-sm text-neutral-800">Beteiligung &amp; Kontakt</Link>
              </div>
              <Link to="/faq" onClick={() => setOpen(false)} className="text-sm text-neutral-800 py-1">FAQ</Link>
              <Link to="/partner" onClick={() => setOpen(false)} className="text-sm text-neutral-800 py-1">Partner</Link>
              <Link to="/kontakt" onClick={() => setOpen(false)} className="text-sm text-neutral-800 py-1">Kontakt</Link>
              <Link to="/liveverkehr" onClick={() => setOpen(false)} className="text-sm font-medium text-[hsl(var(--brand-green-dark))] py-1">LIVE-Verkehr</Link>
              <Link to="/berufsverkehr-ticker" onClick={() => setOpen(false)} className="text-sm font-medium text-[hsl(var(--brand-green-strong))] py-1">Berufsverkehr-Ticker</Link>
              <Link to="/#newsletter" onClick={() => setOpen(false)} className="text-sm text-[hsl(var(--brand-green-strong))] py-1">Newsletter</Link>
            </div>
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-brand-line bg-white mt-8">
        <div className="container-narrow pt-12 pb-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={DETMOLD_LOGO} alt="Detmold" className="h-11 w-auto" />
            <span className="text-[11px] leading-tight text-neutral-700 border-l border-brand-line pl-3">
              Kulturstadt<br />im Teutoburger Wald
            </span>
          </Link>
          <nav className="flex flex-wrap gap-x-10 gap-y-3 text-sm text-neutral-700">
            <Link to="/partner" className="hover:text-[hsl(var(--brand-green-strong))]" data-testid="footer-partner">Partner</Link>
            <Link to="/impressum" className="hover:text-[hsl(var(--brand-green-strong))]" data-testid="footer-impressum">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-[hsl(var(--brand-green-strong))]" data-testid="footer-datenschutz">Datenschutz</Link>
            <Link to="/cookies" className="hover:text-[hsl(var(--brand-green-strong))]" data-testid="footer-cookies">Cookies</Link>
            <button type="button" onClick={openSettings} className="hover:text-[hsl(var(--brand-green-strong))] text-left" data-testid="footer-cookie-settings">Cookie-Einstellungen</button>
            <Link to="/barrierefreiheit" className="hover:text-[hsl(var(--brand-green-strong))]" data-testid="footer-barrierefreiheit">Barrierefreiheit</Link>
            <Link to="/admin/login" className="text-neutral-400 hover:text-neutral-700" data-testid="footer-admin">Admin</Link>
          </nav>
        </div>
        {/* Skyline-Bild — dezent, mit weichem Verlauf nach oben */}
        <div className="relative px-6 sm:px-10 lg:px-16 pb-8 pt-2 overflow-hidden">
          <img
            src={SKYLINE_FOOTER}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="w-full max-w-6xl mx-auto h-auto block select-none"
            style={{
              maxHeight: "180px",
              objectFit: "contain",
              objectPosition: "center bottom",
              opacity: 0.92,
              maskImage:
                "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 25%, rgba(0,0,0,1) 60%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 25%, rgba(0,0,0,1) 60%)",
            }}
          />
        </div>
      </footer>
      <CookieBanner />
      <AccessibilityWidget />
    </div>
  );
}
